import { useState, useRef } from "react";
import { api } from "@/lib/api";

// Shared @mention autocomplete logic for custom composers (DMs, live chat, event chat)
// that can't drop in the full MentionField. Detects a trailing @token, fetches members,
// inserts the picked name inline, and tracks the selected ids for the request payload.
export function useMentions() {
  const [query, setQuery] = useState(null);
  const [results, setResults] = useState([]);
  const picked = useRef([]);
  const timer = useRef(null);

  const onText = (text) => {
    const m = (text || "").match(/(?:^|\s)@([\w][\w .'-]{0,30})$/);
    const q = m ? m[1] : null;
    setQuery(q);
    if (timer.current) clearTimeout(timer.current);
    if (q === null) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await api(`/members?q=${encodeURIComponent(q)}`);
        setResults((r.members || r || []).slice(0, 6));
      } catch { setResults([]); }
    }, 200);
  };

  // Returns the new text with the trailing @token replaced by the full @Name.
  const select = (text, member) => {
    const name = member.full_name;
    const next = (text || "").replace(/(^|\s)@([\w][\w .'-]{0,30})$/, (full, pre) => `${pre}@${name} `);
    picked.current.push({ name, id: member.user_id });
    setQuery(null);
    setResults([]);
    return next;
  };

  // Reconciles picked mentions against the current text and returns the id list.
  const mentionIds = (text) => {
    picked.current = picked.current.filter((mm) => (text || "").includes(`@${mm.name}`));
    return [...new Set(picked.current.map((mm) => mm.id))];
  };

  const reset = () => { picked.current = []; setResults([]); setQuery(null); };

  return { query, results, onText, select, mentionIds, reset };
}
